import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ProToolResultsList } from "@/features/tools/ProToolResultsList";
import type {
  ProToolType,
  ToolResultSummary,
} from "@/features/tools/proToolTypes";

type ToolPageLayoutProps = {
  title: string;
  label: string;
  intro: string;
  canUseProTools: boolean;
  isLoading: boolean;
  error: string | null;
  results: ToolResultSummary[];
  toolType: ProToolType;
  emptyResultsMessage: string;
  children: ReactNode;
};

export function ToolPageLayout({
  title,
  label,
  intro,
  canUseProTools,
  isLoading,
  error,
  results,
  toolType,
  emptyResultsMessage,
  children,
}: ToolPageLayoutProps) {
  return (
    <div className="w-full max-w-full space-y-6">
      <Link
        to="/tools"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Pro Tools
      </Link>

      <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <p className="text-sm font-semibold text-indigo-700">{label}</p>
        <h2 className="mt-2 break-words text-2xl font-semibold tracking-normal text-slate-950">
          {title}
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          {intro}
        </p>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
          <div className="h-40 animate-pulse rounded-md bg-slate-100" />
          <p className="mt-4 text-sm font-medium text-slate-500">
            Loading saved results...
          </p>
        </div>
      ) : canUseProTools ? (
        <>
          {children}

          <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <div>
              <h3 className="break-words text-lg font-semibold text-slate-950">
                Recent {title} results
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Saved results from this tool appear here.
              </p>
            </div>
            <ProToolResultsList
              results={results}
              toolType={toolType}
              emptyMessage={emptyResultsMessage}
            />
          </section>
        </>
      ) : (
        <section className="min-w-0 rounded-lg border border-indigo-200 bg-indigo-50 p-4 sm:p-6">
          <p className="text-sm font-semibold text-indigo-950">
            Included with Pro
          </p>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-indigo-900">
            {intro}
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-indigo-800">
            Upgrade to Pro to unlock this tool and the full commercial planning
            suite.
          </p>
          <Link
            to="/pricing"
            className="mt-5 inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:w-auto"
          >
            Upgrade to Pro
          </Link>
        </section>
      )}
    </div>
  );
}
